/*
 * Native Cognito USER_AUTH choice-based email-code sign-in (PRIMARY participant
 * auth). This is the flow mandated by PROGRAMME-DESIGN-AUTH-POPULATION-MODEL-V1
 * v3 (Duncan-LOCKED) + PRD-AUTH-POPULATION-IMPLEMENTATION-V1: email-code is the
 * PRIMARY sign-in for low-privilege participants; password is demoted to a
 * legacy/optional secondary path (src/auth/cognitoAuth.ts).
 *
 * WHY the native USER_AUTH flow (NOT a custom-auth Lambda emulation, NOT the
 * hosted UI): the WU-04 acceptance requires the flow be built on Cognito's
 * FIRST-CLASS choice-based USER_AUTH (InitiateAuth AuthFlow=USER_AUTH →
 * EMAIL_OTP challenge → RespondToAuthChallenge) so WU-05 passkey (WEB_AUTHN) is
 * PURELY ADDITIVE later — the same USER_AUTH entry point already advertises
 * WEB_AUTHN alongside EMAIL_OTP (ground-truthed live on the stage client
 * 3n3u9f96u5oj455k5niikej2og: AvailableChallenges = ["EMAIL_OTP","WEB_AUTHN"]).
 *
 * WHY direct cognito-idp JSON API over fetch (not amazon-cognito-identity-js):
 * the v6 library has no clean USER_AUTH/EMAIL_OTP surface; the repo already
 * speaks the raw JSON API for exactly this reason (cognitoAuth.globalSignOut,
 * cognitoProfile.ts). The browser calls https://cognito-idp.{region}.amazonaws
 * .com/ directly — it NEVER navigates to *.amazoncognito.com (no hosted UI).
 * The app client is a public SPA client (no client secret ⇒ no SECRET_HASH).
 *
 * The emailed code is the DELIBERATE security floor (Duncan build-caveat (c)):
 * control-of-mailbox is sufficient WHILE participants are low-privilege. EMAIL_OTP
 * additionally REQUIRES a verified email (email_verified=true) — see the
 * EmailUnverified surface below and the WU-04 email_verified audit.
 */
import { readAuthConfig } from './authConfig'
import { AuthFlowError, normaliseError } from './cognitoAuth'
import { normalizeEmail } from './normalizeEmail'
import type { TokenSet } from './oidc'

const TARGET_INITIATE = 'AWSCognitoIdentityProviderService.InitiateAuth'
const TARGET_RESPOND = 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'

/** Result of requesting an email code — carries the opaque Cognito Session that
 *  the subsequent RespondToAuthChallenge MUST echo back, plus the masked
 *  delivery destination (e.g. "j***@e***") Cognito returns for display. */
export interface EmailCodeChallenge {
  session: string
  /** Masked destination from ChallengeParameters.CODE_DELIVERY_DESTINATION. */
  destination?: string
}

interface CognitoApiError {
  __type?: string
  message?: string
  Message?: string
}

/** POST a JSON-1.1 cognito-idp action. On a non-2xx the body is a
 *  {__type, message} error object which we normalise through the SAME
 *  AuthFlowError surface the password path uses (cognitoAuth.normaliseError),
 *  so the UI keeps mapping ONE typed error set — never raw AWS names. */
async function callCognito(
  target: string,
  body: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<Record<string, unknown>> {
  const config = readAuthConfig()
  if (!config) throw new AuthFlowError('Unknown', 'auth not configured')
  let res: Response
  try {
    res = await fetchImpl(`https://cognito-idp.${config.region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': target,
      },
      body: JSON.stringify(body),
    })
  } catch {
    // A thrown fetch is a transport failure (offline/DNS) — map to Network so
    // the UI shows the "check your connection" copy, never a dead spinner.
    throw new AuthFlowError('Network', 'Network problem — check your connection and try again.')
  }
  const text = await res.text().catch(() => '')
  const json = text ? (JSON.parse(text) as Record<string, unknown>) : {}
  if (!res.ok) {
    const err = json as CognitoApiError
    const rawType = (err.__type || '').split('#').pop() || ''
    const message = err.message || err.Message || 'Something went wrong. Please try again.'
    throw normaliseError({ name: rawType, message })
  }
  return json
}

function requireClientId(): string {
  const config = readAuthConfig()
  if (!config) throw new AuthFlowError('Unknown', 'auth not configured')
  return config.userPoolClientId
}

/**
 * Request an emailed sign-in code via the native USER_AUTH choice-based flow.
 *
 * InitiateAuth AuthFlow=USER_AUTH with PREFERRED_CHALLENGE=EMAIL_OTP asks
 * Cognito to go straight to the email code (skipping SELECT_CHALLENGE); the
 * branded email is composed + sent by the pool's already-live CustomEmailSender
 * (WU-01). The username is normalised (lowercased) at this boundary exactly like
 * every other outbound auth call (normalizeEmail) so the case-sensitive pool is
 * addressed consistently.
 *
 * ANTI-ENUMERATION: the pool has PreventUserExistenceErrors enabled, so an
 * UNKNOWN email returns a SIMULATED EMAIL_OTP challenge (masked destination, no
 * email ever sent) rather than an error — this function therefore resolves the
 * same for a real and a non-existent account, and the UI must say "we sent you
 * a code" either way (existence is never leaked). A submitted code for a
 * non-existent account simply never matches.
 *
 * UNVERIFIED EMAIL (build-caveat (c) / audit): EMAIL_OTP requires
 * email_verified=true. If the account exists but the email is unverified, the
 * pool does NOT offer EMAIL_OTP among AvailableChallenges — we surface a
 * DISTINCT 'EmailUnverified' error so the UI can give a helpful, honest state
 * instead of a generic failure.
 */
export async function requestEmailCode(email: string, fetchImpl: typeof fetch = fetch): Promise<EmailCodeChallenge> {
  const username = normalizeEmail(email)
  const first = await callCognito(TARGET_INITIATE, {
    AuthFlow: 'USER_AUTH',
    ClientId: requireClientId(),
    AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  }, fetchImpl)

  const challengeName = first.ChallengeName as string | undefined
  const available = (first.AvailableChallenges as string[] | undefined) ?? []

  if (challengeName === 'EMAIL_OTP') {
    return { session: first.Session as string, destination: destinationOf(first) }
  }

  // Defensive: some pool configs answer USER_AUTH with SELECT_CHALLENGE first.
  // If EMAIL_OTP is available, ANSWER it to obtain the EMAIL_OTP challenge +
  // a fresh Session; if it is NOT available, an existing account is telling us
  // the email cannot receive an OTP (unverified) — surface that distinctly.
  if (challengeName === 'SELECT_CHALLENGE' || (challengeName == null && available.length)) {
    if (!available.includes('EMAIL_OTP')) {
      throw new AuthFlowError('EmailUnverified', 'This account cannot receive an email code yet — its email address is not verified.')
    }
    const selected = await callCognito(TARGET_RESPOND, {
      ChallengeName: 'SELECT_CHALLENGE',
      ClientId: requireClientId(),
      Session: first.Session as string,
      ChallengeResponses: { USERNAME: username, ANSWER: 'EMAIL_OTP' },
    }, fetchImpl)
    if ((selected.ChallengeName as string | undefined) !== 'EMAIL_OTP') {
      throw new AuthFlowError('Unknown', 'Could not start the email-code sign-in.')
    }
    return { session: selected.Session as string, destination: destinationOf(selected) }
  }

  throw new AuthFlowError('Unknown', 'Could not start the email-code sign-in.')
}

function destinationOf(challenge: Record<string, unknown>): string | undefined {
  const params = challenge.ChallengeParameters as Record<string, string> | undefined
  return params?.CODE_DELIVERY_DESTINATION
}

/**
 * Answer the EMAIL_OTP challenge with the user-entered code and receive tokens.
 * The Session from requestEmailCode MUST be echoed back verbatim. On success the
 * native AuthenticationResult maps 1:1 onto the existing TokenSet, so session.ts
 * stays the single session source of truth (the access token intrinsically
 * carries aws.cognito.signin.user.admin, so Profile GetUser/UpdateUserAttributes
 * work unchanged — same guarantee as the SRP path).
 *
 * Error surface (typed, never raw AWS names):
 *  - CodeMismatch  → the entered code is wrong (the same Session may be retried).
 *  - ExpiredCode / NotAuthorized → the code/session has lapsed; request a fresh
 *    code (the UI restarts the send).
 */
export async function submitEmailCode(
  email: string,
  code: string,
  session: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TokenSet> {
  const username = normalizeEmail(email)
  const res = await callCognito(TARGET_RESPOND, {
    ChallengeName: 'EMAIL_OTP',
    ClientId: requireClientId(),
    Session: session,
    ChallengeResponses: { USERNAME: username, EMAIL_OTP_CODE: code.trim() },
  }, fetchImpl)

  const auth = res.AuthenticationResult as
    | { IdToken: string; AccessToken: string; RefreshToken?: string; ExpiresIn: number }
    | undefined
  if (!auth?.IdToken || !auth?.AccessToken) {
    // A further challenge (e.g. MFA) is out of scope for the participant floor;
    // never claim signed-in without tokens.
    throw new AuthFlowError('Unknown', 'Sign-in did not complete — please try again.')
  }
  return {
    idToken: auth.IdToken,
    accessToken: auth.AccessToken,
    refreshToken: auth.RefreshToken,
    expiresIn: auth.ExpiresIn,
  }
}

/** requestEmailCode is idempotent from the caller's view — a resend is simply a
 *  fresh InitiateAuth (a NEW code + NEW Session; the previous one is discarded).
 *  Named for call-site clarity. */
export const resendEmailCode = requestEmailCode

// ---------------------------------------------------------------------------
// Deep-link prefill (LOCKED canon behaviour: PRE-FILL, NEVER auto-submit)
// ---------------------------------------------------------------------------

export interface SignInDeepLink {
  /** The emailed sign-in code, URL-decoded, verbatim (length-agnostic). */
  code: string
  /** OPTIONAL opaque continuation hint (`u`) — captured verbatim if present,
   *  never required and never acted on here; absent/unknown is fine. */
  u?: string
}

// ---------------------------------------------------------------------------
// Pending-challenge persistence (deep-link + navigation survival)
// ---------------------------------------------------------------------------

/**
 * A Cognito EMAIL_OTP code can only be answered against the opaque `Session`
 * returned by the InitiateAuth that SENT it. A branded-email deep-link opened
 * in a fresh tab (common: request the code on a phone, tap the email button on
 * the same phone) would otherwise have no Session in memory. We persist the
 * pending {email, session} to localStorage (shared across same-origin tabs) so
 * the code-entry step — deep-linked or navigated — can complete the SAME
 * challenge that emailed the code, instead of silently re-issuing (which would
 * invalidate the code the user is holding). Cleared on success/failure; the
 * value is a short-lived opaque challenge token, never a credential.
 *
 * A deep-link opened on a DIFFERENT device has no persisted session — the
 * code-entry step then honestly offers "resend a fresh code" rather than failing
 * opaquely.
 */
const PENDING_KEY = 'mytrumin.emailcode.v1'

export interface PendingEmailCode {
  email: string
  session: string
}

export function storePendingEmailCode(pending: PendingEmailCode): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending))
  } catch {
    // no-op: private-mode / storage-disabled degrades to in-memory session only
  }
}

export function loadPendingEmailCode(): PendingEmailCode | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as PendingEmailCode
    return p && typeof p.email === 'string' && typeof p.session === 'string' ? p : null
  } catch {
    return null
  }
}

export function clearPendingEmailCode(): void {
  try {
    localStorage.removeItem(PENDING_KEY)
  } catch {
    // no-op
  }
}

/**
 * Parse the participant sign-in-code deep-link's query string into { code, u? }
 * for the code-entry step to PRE-FILL. PURE: returns data only, NEVER submits —
 * the "lands pre-filled and never auto-submits" canon (PROGRAMME-DESIGN-AUTH-
 * POPULATION-MODEL-V1 v3) is enforced by the caller, which sets state from this
 * and waits for an explicit user action.
 *
 * PINNED to the settings-side SINGLE-SOURCE contract (BOARDROOM-2026-09-19-
 * 211648: buildSignInCodeDeepLink / parseCodeFromSearch sharing
 * SIGN_IN_CODE_QUERY_KEY). The participant link is EXACTLY
 *     <env-origin>/sign-in?code=<url-encoded-code>[&u=<opaque continuation hint>]
 * (stage https://stage-my.trumin.com/sign-in?code=… ; prod https://my.trumin.com/…).
 * ONE code key: `code`. There is NO email param (and by construction no
 * submit/confirm/go flag) — anything else in the query is ignored (the older
 * tolerant `otp`/`sign_in_code`/`c` aliases + `?email=` handling are RETIRED).
 *
 * LENGTH-AGNOSTIC: the engine passes Cognito's code through verbatim (EMAIL_OTP
 * is 6 digits per Cognito today, but the length is NOT hard-pinned). We pin on
 * key=code and tolerate whatever value it carries — with two exceptions that are
 * NOT usable codes and must NOT prefill garbage, so both land on the normal
 * request-a-code step instead:
 *   (1) an empty / whitespace-only value; and
 *   (2) a verbatim Cognito NATIVE-TEMPLATE placeholder token (e.g. `{####}`) —
 *       in native-template mode Cognito can preserve the `{####}` placeholder
 *       into the link un-substituted, and prefilling it would drop "{####}"
 *       into the code box (a real code never contains braces).
 *
 * Path=/sign-in is served by the SPA gate: for any UNAUTHED path the app renders
 * <Login/>, which reads window.location.search and calls this (see src/App.tsx);
 * the infra-owned R2/Cloudflare deploy already serves index.html for arbitrary
 * deep paths (/orders/x, /claim/:t, /auth/callback), so /sign-in never 404s.
 */
export function parseSignInDeepLink(search: string): SignInDeepLink | null {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return null
  }
  const code = (params.get('code') ?? '').trim() // URLSearchParams URL-decodes
  // Empty/whitespace, or an un-substituted `{…}` native-template placeholder ⇒
  // not a usable code (a real Cognito code never carries braces).
  if (!code || /[{}]/.test(code)) return null
  const u = (params.get('u') ?? '').trim()
  return u ? { code, u } : { code }
}
